import { Link, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { Button } from '../components/ui/button';
import { JamInventraLogoFull } from '../components/JamInventraLogo';
import { 
  Warehouse, 
  BarChart3, 
  Shield, 
  Zap, 
  Globe,
  ArrowRight,
  Check,
  ChevronRight,
  Package,
  Loader2
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import WhatsAppButton from '../components/WhatsAppButton';

export default function LandingPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [demoLoading, setDemoLoading] = useState(false);

  const handleViewDemo = async () => {
    setDemoLoading(true);
    try {
      await login('demo@jaminventra.com', 'demo123');
      navigate('/app/dashboard');
    } catch (error) {
      console.error('Demo login failed:', error);
      // If auto-login fails, redirect to login page
      navigate('/login');
    } finally {
      setDemoLoading(false);
    }
  };

  const features = [
    {
      icon: Package,
      title: "Smart Stock Tracking",
      description: "Know exactly what items you have in stock at any time. Track products across your shop, storeroom, or market stall — no more notebooks or guesswork."
    },
    {
      icon: BarChart3,
      title: "Know When Stock Is Running Low",
      description: "Get alerts when products are almost finished so you can reorder before losing sales."
    },
    {
      icon: Shield,
      title: "Easy Profit & Cost Tracking",
      description: "Automatically calculate cost price, selling price, and profit so you always know how much your business is making."
    },
    {
      icon: Warehouse,
      title: "Works for Small Shops & Vendors",
      description: "Perfect for phone stores, clothing vendors, supermarkets, beauty stores, and farmers across Jamaica."
    },
    {
      icon: Zap,
      title: "Fast Sales & Stock Updates",
      description: "Update inventory instantly when you sell, receive stock, or adjust items — even from your phone."
    },
    {
      icon: Globe,
      title: "Simple Reports for Your Business",
      description: "See clear reports showing best selling items, slow moving products, total stock value, and daily sales."
    }
  ];

  const jamaicaFeatures = [
    "Manage stock for multiple shop locations",
    "Track items sold through in-store, WhatsApp, or online",
    "Know exactly what to reorder from suppliers",
    "Stop losing money from missing inventory",
    "Generate receipts and track customer sales",
    "Works on phone, tablet, laptop, or computer"
  ];

  const problems = [
    {
      problem: "Writing stock in notebooks",
      solution: "Digital tracking that updates automatically"
    },
    {
      problem: "Running out of popular items",
      solution: "Low stock alerts before you lose sales"
    },
    {
      problem: "Not knowing true profit",
      solution: "Automatic profit calculation on every sale"
    },
    {
      problem: "Losing track across locations",
      solution: "See all your stock in one place"
    }
  ];

  const plans = [
    {
      name: "Starter",
      price: "J$2,499",
      period: "/month",
      description: "For new small businesses",
      features: ["1 User", "Inventory Tracking", "Offline/Mobile Access", "Basic Reports", "Low Stock Alerts"],
      highlighted: false
    },
    {
      name: "Growth",
      price: "J$4,999",
      period: "/month",
      description: "For growing businesses",
      features: ["Up to 5 Users", "Point of Sale (POS)", "WhatsApp/Email Receipts", "Sales & Profit Tracking", "Top-Selling Items", "Stock History"],
      highlighted: true
    },
    {
      name: "Pro",
      price: "J$9,999",
      period: "/month",
      description: "For established businesses",
      features: ["Multi-Location Support", "Advanced Analytics", "Role-Based Access", "Priority Support", "Everything in Growth"],
      highlighted: false
    }
  ];

  return (
    <div className="min-h-screen bg-[#F5F5F7]">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-[#E5E5EA]">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-1.5 hover:opacity-80 transition-opacity mr-2" data-testid="back-to-lom">
              <img src="/lom-group-logo.png" alt="LOM Solutions" className="h-8 w-auto rounded" />
            </Link>
            <div className="w-px h-6 bg-[#E5E5EA] hidden sm:block" />
            <Link to="/jaminventra" className="flex items-center gap-2">
              <JamInventraLogoFull height={50} />
            </Link>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/login">
              <Button variant="ghost" className="text-[#0A0A0A] hover:bg-[#F5F5F7]" data-testid="nav-login-btn">
                Log In
              </Button>
            </Link>
            <Link to="/register">
              <Button className="bg-[#007AFF] hover:bg-[#0066D6] text-white btn-hover" data-testid="nav-register-btn">
                Start Free Trial
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-24 overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-10"
          style={{ backgroundImage: "url('https://images.unsplash.com/photo-1764795850513-fa8a469a4600?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwxfHxibGFjayUyMGJ1c2luZXNzJTIwb3duZXIlMjByZXRhaWwlMjBzaG9wJTIwY291bnRlciUyMGludmVudG9yeSUyMHByb2R1Y3RzfGVufDB8fHx8MTc3ODA5ODIwM3ww&ixlib=rb-4.1.0&q=85')" }}
        />
        <div className="relative max-w-7xl mx-auto px-6">
          <div className="max-w-3xl">
            <p className="text-overline text-[#007AFF] mb-4">INVENTORY MANAGEMENT FOR JAMAICAN BUSINESSES</p>
            <h1 className="text-heading-black text-5xl sm:text-6xl lg:text-7xl text-[#0A0A0A] tracking-tight mb-6">
              JamInventra
            </h1>
            <p className="text-lg text-[#0A0A0A]/70 mb-8 max-w-xl">
              Built in Jamaica, for Jamaicans. Stop losing money from missing stock. Track inventory, know your profit, and grow your business — from any device.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/register">
                <Button size="lg" className="bg-[#0A0A0A] hover:bg-[#1C1C1E] text-white btn-hover h-12 px-8 text-base" data-testid="hero-cta-btn">
                  Start 30-Day Free Trial <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
              <Button 
                size="lg" 
                variant="outline" 
                className="border-[#0A0A0A] text-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white h-12 px-8 text-base"
                onClick={handleViewDemo}
                disabled={demoLoading}
              >
                {demoLoading ? (
                  <>
                    <Loader2 className="mr-2 w-4 h-4 animate-spin" />
                    Loading Demo...
                  </>
                ) : (
                  'View Demo'
                )}
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <p className="text-overline text-[#007AFF] mb-4">FEATURES</p>
            <h2 className="text-heading-black text-4xl sm:text-5xl text-[#0A0A0A] tracking-tight">
              EVERYTHING YOUR BUSINESS NEEDS
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <div 
                key={index} 
                className="p-8 border border-[#E5E5EA] bg-white hover:border-[#007AFF] transition-colors duration-200 group"
              >
                <div className="w-12 h-12 bg-[#F5F5F7] group-hover:bg-[#007AFF]/10 flex items-center justify-center mb-6 transition-colors duration-200">
                  <feature.icon className="w-6 h-6 text-[#0A0A0A] group-hover:text-[#007AFF]" />
                </div>
                <h3 className="text-heading text-xl text-[#0A0A0A] mb-3">{feature.title}</h3>
                <p className="text-[#0A0A0A]/60">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Built for Jamaica Section */}
      <section className="py-20 bg-gradient-to-br from-[#007AFF] to-[#0055D4]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-white/70 text-sm font-medium tracking-wider uppercase mb-4">Made for Jamaica</p>
              <h2 className="text-heading-black text-3xl sm:text-4xl text-white mb-6">
                Built for Jamaican Small Businesses
              </h2>
              <p className="text-white/80 text-lg mb-8">
                Whether you run a phone shop, a clothing store, or a farm produce stall — this system was built for you.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {jamaicaFeatures.map((feature, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <div className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-3 h-3 text-white" />
                    </div>
                    <span className="text-white/90 text-sm">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8">
              <h3 className="text-white font-bold text-xl mb-6">Problems We Solve</h3>
              <div className="space-y-4">
                {problems.map((item, index) => (
                  <div key={index} className="flex items-center gap-4 bg-white/10 rounded-lg p-4">
                    <div className="flex-1">
                      <p className="text-white/60 text-sm line-through">{item.problem}</p>
                      <p className="text-white font-medium">{item.solution}</p>
                    </div>
                    <ArrowRight className="w-5 h-5 text-white/50" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Screenshot Section */}
      <section className="py-24 bg-[#0A0A0A] relative noise-overlay">
        <div className="relative max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-overline text-[#007AFF] mb-4">EASY TO USE</p>
              <h2 className="text-heading-black text-4xl sm:text-5xl text-white tracking-tight mb-6">
                MANAGE FROM ANY DEVICE
              </h2>
              <p className="text-white/60 text-lg mb-8">
                Access your inventory from anywhere — on your phone, tablet, laptop, or desktop computer. Your business data syncs across all devices.
              </p>
              <ul className="space-y-4">
                {["Scan barcodes with your phone camera", "See stock levels instantly", "Get alerts when products run low", "Track daily sales and profits"].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-white/80">
                    <div className="w-5 h-5 rounded-full bg-[#34C759] flex items-center justify-center">
                      <Check className="w-3 h-3 text-white" />
                    </div>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="relative">
              <img 
                src="https://images.pexels.com/photos/19838484/pexels-photo-19838484.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Small business owner smiling behind store counter with products"
                className="rounded-sm border border-white/10"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-24 bg-white" id="pricing">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <p className="text-overline text-[#007AFF] mb-4">PRICING</p>
            <h2 className="text-heading-black text-4xl sm:text-5xl text-[#0A0A0A] tracking-tight">
              SIMPLE, TRANSPARENT PRICING
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {plans.map((plan, index) => (
              <div 
                key={index}
                className={`p-8 border ${plan.highlighted ? 'border-[#007AFF] bg-[#007AFF]/5' : 'border-[#E5E5EA] bg-white'} relative`}
              >
                {plan.highlighted && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#007AFF] text-white text-xs font-semibold px-3 py-1">
                    MOST POPULAR
                  </div>
                )}
                <h3 className="text-heading text-2xl text-[#0A0A0A] mb-2">{plan.name}</h3>
                <p className="text-[#0A0A0A]/60 text-sm mb-6">{plan.description}</p>
                <div className="mb-6">
                  <span className="text-heading-black text-4xl text-[#0A0A0A]">{plan.price}</span>
                  <span className="text-[#0A0A0A]/60">{plan.period}</span>
                </div>
                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-[#0A0A0A]/80">
                      <Check className="w-4 h-4 text-[#34C759]" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <Link to="/register">
                  <Button 
                    className={`w-full btn-hover ${plan.highlighted ? 'bg-[#007AFF] hover:bg-[#0066D6] text-white' : 'bg-[#0A0A0A] hover:bg-[#1C1C1E] text-white'}`}
                    data-testid={`pricing-${plan.name.toLowerCase()}-btn`}
                  >
                    Get Started <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-[#F5F5F7]">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-heading-black text-4xl sm:text-5xl text-[#0A0A0A] tracking-tight mb-6">
            READY TO GROW YOUR BUSINESS?
          </h2>
          <p className="text-[#0A0A0A]/60 text-lg mb-8">
            Join hundreds of Jamaican businesses already using JamInventra. Start your 30-day free trial — no credit card required.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register">
              <Button size="lg" className="bg-[#007AFF] hover:bg-[#0066D6] text-white btn-hover h-12 px-8 text-base" data-testid="cta-register-btn">
                Start Free Trial <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
            <a href="https://wa.me/18764507828" target="_blank" rel="noopener noreferrer">
              <Button size="lg" variant="outline" className="border-[#25D366] text-[#25D366] hover:bg-[#25D366] hover:text-white h-12 px-8 text-base">
                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                WhatsApp Us
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0A0A0A] py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <JamInventraLogoFull height={40} variant="light" />
              <span className="text-white/30 text-xs border-l border-white/10 pl-4">A product of LOM Solutions Jamaica Limited</span>
            </div>
            <div className="flex items-center gap-6 flex-wrap">
              <a href="https://wa.me/18764507828" className="text-white/60 hover:text-white text-sm">
                876-450-7828
              </a>
              <a href="https://wa.me/18763773479" className="text-white/60 hover:text-white text-sm">
                876-377-3479
              </a>
            </div>
            <p className="text-white/40 text-sm">
              &copy; 2026 LOM Solutions Jamaica Limited. All rights reserved.
            </p>
          </div>
        </div>
      </footer>

      {/* Floating WhatsApp Button */}
      <WhatsAppButton />
    </div>
  );
}
